import { Add, DeleteOutline, LocalOffer } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import TagsAutocomplete from "../../components/TagsAutocomplete";
import {
  LIST_ADD_TAGS_MODE_ADD,
  LIST_ADD_TAGS_MODE_REMOVE,
} from "../../constants";
import { ListConditions } from "../../types";
import ListActionConditions from "./ListActionConditions";

type TagGroup = {
  mode: string;
  tags: string[];
  conditions: ListConditions;
};

const emptyGroup = (): TagGroup => ({
  mode: LIST_ADD_TAGS_MODE_ADD,
  tags: [],
  conditions: { combinator: "AND", filters: [], groups: [] },
});

export default ({
  actionParameters,
  setActionParameters,
  companyId,
  getToken,
  setValidForm,
  headers,
}: {
  actionParameters: any;
  setActionParameters: (p: any) => void;
  companyId: string;
  getToken: () => Promise<string>;
  setValidForm: (b: boolean) => void;
  headers: Array<any>;
}) => {
  const tagGroups: TagGroup[] = actionParameters.tagGroups || [];

  useEffect(() => {
    if (!actionParameters.tagGroups) {
      setActionParameters({
        ...actionParameters,
        tagGroups: [emptyGroup()],
      });
    }
  }, []);

  useEffect(() => {
    const valid =
      tagGroups.length > 0 &&
      tagGroups.every((g) => Array.isArray(g.tags) && g.tags.length > 0);
    setValidForm(valid);
  }, [JSON.stringify(tagGroups)]);

  const updateGroup = (index: number, patch: Partial<TagGroup>) => {
    const next = tagGroups.map((g, i) => (i === index ? { ...g, ...patch } : g));
    setActionParameters({ ...actionParameters, tagGroups: next });
  };

  const addGroup = () => {
    setActionParameters({
      ...actionParameters,
      tagGroups: [...tagGroups, emptyGroup()],
    });
  };

  const removeGroup = (index: number) => {
    const next = tagGroups.filter((_, i) => i !== index);
    setActionParameters({
      ...actionParameters,
      tagGroups: next.length ? next : [emptyGroup()],
    });
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="subtitle1"
        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
      >
        <LocalOffer sx={{ fontSize: 18, color: "text.secondary" }} />
        Tag Groups
        <Tooltip
          title="Each group runs independently against every row. When the group's conditions match, its tags are added to or removed from the row's global Tags column. Tags are stored on the row itself, so multiple actions and groups all mutate the same list and tags follow rows when copied or moved between lists."
        >
          <Typography
            component="span"
            variant="caption"
            sx={{ color: "text.secondary", cursor: "help" }}
          >
            (?)
          </Typography>
        </Tooltip>
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>
        Add multiple condition groups. Each one independently adds or removes
        tags on rows that match.
      </Typography>

      {tagGroups.map((group, i) => (
        <Paper
          key={i}
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "action.hover",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <Select
              size="small"
              value={group.mode || LIST_ADD_TAGS_MODE_ADD}
              onChange={(e) => updateGroup(i, { mode: e.target.value })}
            >
              <MenuItem value={LIST_ADD_TAGS_MODE_ADD}>Add Tags</MenuItem>
              <MenuItem value={LIST_ADD_TAGS_MODE_REMOVE}>
                Remove Tags
              </MenuItem>
            </Select>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Remove this tag group">
              <span>
                <IconButton
                  size="small"
                  onClick={() => removeGroup(i)}
                  disabled={tagGroups.length === 1}
                >
                  <DeleteOutline
                    color={tagGroups.length === 1 ? "disabled" : "error"}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <TagsAutocomplete
            value={group.tags || []}
            onChange={(tags) => updateGroup(i, { tags })}
            companyId={companyId}
            getToken={getToken}
            label={
              group.mode === LIST_ADD_TAGS_MODE_REMOVE
                ? "Tags to remove"
                : "Tags to add"
            }
            placeholder="Type a tag and press Enter"
          />

          <ListActionConditions
            headers={headers}
            conditions={
              group.conditions || {
                combinator: "AND",
                filters: [],
                groups: [],
              }
            }
            setConditions={(conditions) => updateGroup(i, { conditions })}
          />
        </Paper>
      ))}

      <Button variant="outlined" startIcon={<Add />} onClick={addGroup}>
        Add Tag Group
      </Button>
    </Box>
  );
};
